// BugReportDialog.tsx — a small "Report a bug" form in Settings. The user types what
// went wrong (and optionally their email so we can reply); on send it POSTs to the
// Worker, which emails support@renurture.org via Resend, tagged as a bug report.
// House style: no emoji, no em-dash.
import React from 'react';
import { Display, Body, Btn } from '@/components/ui';
import { reportBug } from '@/services/reportBug';

export function BugReportDialog({ onClose }: { onClose: () => void }) {
  const [message, setMessage] = React.useState('');
  // Start empty so the user enters their own email (no pre-fill from the session).
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const ready = message.trim().length >= 3 && !busy;

  const onSend = async () => {
    if (!ready) return;
    setBusy(true); setError(null);
    const ok = await reportBug(message, email);
    setBusy(false);
    if (ok) setSent(true);
    else setError('Could not send right now. Please email support@renurture.org and we will help.');
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Report a bug" style={overlay}>
      <div style={panel}>
        {sent ? (
          <>
            <Display text="Thank you" size={26} weight={400} style={{ marginBottom: 12 }} />
            <Body size={14.5} style={{ marginBottom: 22, lineHeight: 1.6 }}>
              Your report has been sent to our team. If you left your email, we will get back to you.
            </Body>
            <Btn full size="lg" onClick={onClose}>Close</Btn>
          </>
        ) : (
          <>
            <Display text="Report a bug" size={26} weight={400} style={{ marginBottom: 12 }} />
            <Body size={14.5} style={{ marginBottom: 14, lineHeight: 1.6 }}>
              Something not working as expected? Tell us what happened and we will look into it. This goes straight to our support team.
            </Body>

            <Body size={13} style={{ margin: '0 0 6px' }}>What went wrong?</Body>
            <textarea
              value={message}
              onChange={(e) => { setMessage(e.target.value); setError(null); }}
              placeholder="Describe the problem, and what you were doing when it happened."
              rows={5}
              aria-label="Describe the problem"
              style={textareaStyle}
            />

            <Body size={13} style={{ margin: '14px 0 6px' }}>Your email (optional, so we can reply)</Body>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-label="Your email (optional)"
              style={field}
            />

            {error && (
              <Body role="alert" size={13} style={{ marginTop: 10, color: '#8B2F1A', lineHeight: 1.5 }}>{error}</Body>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <Btn full variant="ghost" size="lg" onClick={onClose} disabled={busy}>Cancel</Btn>
              <Btn full size="lg" onClick={() => void onSend()} disabled={!ready} ariaLabel="Send bug report">
                {busy ? 'Sending…' : 'Send report'}
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1100,
  background: 'rgba(20,16,12,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  paddingTop: 'calc(var(--safe-top) + 24px)',
  paddingBottom: 'calc(var(--safe-bottom) + 24px)',
};
const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: 'var(--card-top, #fff)',
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.1))',
  borderRadius: 20,
  padding: 24,
  textAlign: 'left',
  maxHeight: '90vh',
  overflowY: 'auto',
};
const field: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '13px 14px',
  borderRadius: 13,
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.18))',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 15,
  color: 'var(--ink)',
  background: 'var(--card-bot, #fff)',
};
const textareaStyle: React.CSSProperties = {
  ...field,
  resize: 'vertical',
  minHeight: 96,
  lineHeight: 1.5,
};
